import React from 'react';
import type { TourStep } from './types';

export const designsSteps: TourStep[] = [
  {
    target: 'body',
    title: 'Добро пожаловать в раздел "Дизайн макетов"',
    content: 'Здесь вы управляете всеми дизайн-макетами для ваших товаров: загрузка файлов, редактирование метаданных, связывание с товарами.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-tabs"]',
    title: 'Фильтрация по комплектности',
    content: (
      <div>
        <p className="mb-2">Переключайтесь между вкладками для просмотра дизайнов:</p>
        <ul className="list-disc pl-4 space-y-1 text-sm">
          <li><strong>Все дизайны</strong> — показывает весь каталог</li>
          <li><strong>Неполный комплект</strong> — только дизайны, для которых не созданы все товары</li>
        </ul>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-incomplete-tab"]',
    title: 'Неполные комплекты',
    content: (
      <div>
        <p className="mb-2 font-medium">Что такое неполный комплект?</p>
        <p className="mb-2 text-sm">Каждый дизайн привязан к типу (например, "Праздники"). Тип дизайна определяет, какие категории товаров должны быть созданы.</p>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2 text-sm">
          <p className="font-medium text-amber-800">📦 Пример:</p>
          <p className="text-amber-700">Тип "Праздники" требует создания товаров:</p>
          <ul className="list-disc pl-4 mt-1 text-amber-700">
            <li>Топперы (1 шт)</li>
            <li>Леденец на палочке (1 шт)</li>
            <li>Декор для выпечки (1 шт)</li>
            <li>Конфеты (1 шт)</li>
          </ul>
          <p className="mt-1 text-amber-700">Если создано только 3 из 4 — комплект <strong>неполный</strong>.</p>
        </div>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-completeness-header"]',
    title: 'Статус комплекта',
    content: (
      <div>
        <p className="mb-2">В этом столбце отображается прогресс создания товаров:</p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">3 из 3 - 100%</span>
            <span>✅ Комплект полный</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">2 из 3 - 67%</span>
            <span>⚠️ Частично создан</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">1 из 3 - 33%</span>
            <span>❌ Критически неполный</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">0 из 3 - 0%</span>
            <span>⬜ Товары не созданы</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">💡 Нажмите на статус для просмотра деталей</p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: 'Почему комплект может быть неполным?',
    content: (
      <div>
        <p className="mb-2 font-medium">Причины неполного комплекта:</p>
        <ol className="list-decimal pl-4 space-y-2 text-sm">
          <li>
            <strong>Не все товары созданы</strong>
            <p className="text-gray-600">При импорте дизайна товары создаются автоматически только для указанных категорий</p>
          </li>
          <li>
            <strong>Товар был удалён</strong>
            <p className="text-gray-600">Если товар был удалён из каталога, комплект станет неполным</p>
          </li>
          <li>
            <strong>Изменился тип дизайна</strong>
            <p className="text-gray-600">При смене типа дизайна требуемые категории могут измениться</p>
          </li>
          <li>
            <strong>Обновилась матрица типов</strong>
            <p className="text-gray-600">Администратор мог добавить новые категории для типа дизайна</p>
          </li>
        </ol>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: 'Как исправить неполный комплект?',
    content: (
      <div>
        <p className="mb-2 font-medium">Способы дополнения комплекта:</p>
        <div className="space-y-3 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded p-2">
            <p className="font-medium text-blue-800">1. Автоматическое создание</p>
            <p className="text-blue-700">Выберите дизайны → меню "Действия" → "Создать недостающие товары"</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded p-2">
            <p className="font-medium text-purple-800">2. Ручное создание</p>
            <p className="text-purple-700">Откройте дизайн → посмотрите недостающие категории → создайте товары вручную</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded p-2">
            <p className="font-medium text-green-800">3. Массовое создание при импорте</p>
            <p className="text-green-700">При импорте дизайнов установите флаг "Создавать товары автоматически"</p>
          </div>
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-search"]',
    title: 'Поиск дизайнов',
    content: 'Ищите дизайны по коду (design_code), названию бренда, теме или другим параметрам. Поиск работает мгновенно.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-filter-type"]',
    title: 'Фильтр по типу',
    content: 'Фильтруйте дизайны по типу: иллюстрация, фото, паттерн и т.д. Это поможет быстро найти нужный макет.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-actions"]',
    title: 'Действия с дизайнами',
    content: (
      <div>
        <p className="mb-2">Меню с массовыми операциями:</p>
        <ul className="list-disc pl-4 space-y-1 text-sm">
          <li><strong>Импорт</strong> — загрузка дизайнов из Excel/CSV</li>
          <li><strong>Экспорт</strong> — выгрузка выбранных дизайнов</li>
          <li><strong>Проверить комплектность</strong> — обновить статусы</li>
          <li><strong>Создать товары</strong> — дополнить комплекты</li>
        </ul>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-create-btn"]',
    title: 'Создание дизайнов',
    content: 'Нажмите для создания новых дизайн-макетов. Вы сможете загрузить файлы и заполнить все метаданные.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-grid"]',
    title: 'Таблица дизайнов',
    content: 'Список всех дизайнов с превью, кодом, брендом и статусом комплектности. Кликните на строку для просмотра подробностей.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="designs-pagination"]',
    title: 'Навигация',
    content: 'Используйте пагинацию для просмотра всех дизайнов. Измените количество на странице по необходимости.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: 'body',
    title: '🎉 Отлично! Вы готовы к работе',
    content: (
      <div>
        <p className="mb-2">Теперь вы знаете:</p>
        <ul className="list-disc pl-4 space-y-1 text-sm">
          <li>Что такое комплектность дизайна</li>
          <li>Почему комплекты могут быть неполными</li>
          <li>Как исправить неполные комплекты</li>
          <li>Как искать и фильтровать дизайны</li>
        </ul>
        <p className="mt-3 text-sm font-medium">Для создания дизайнов нажмите кнопку "Создать макеты"!</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];
